import { Component, computed, effect, inject, input, untracked } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { DisplayType } from '@models/module/fields/enums';
import { AddressModel, getAddressModelFromAzureMapAddressAndPosition } from '@models/module/fields/main/address.model';
import { AzureMapAddressSearchResult } from '@shared/components/azure-map/azure-map.model';
import { AzureMapService } from '@shared/components/azure-map/azure-map.service';
import { CipoControlBase } from '@shared/components/fields/cipo-control.base';
import { CipoAddressConfig } from '@shared/components/fields/common';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { ConfirmationDialog } from '@shared/dialogs';
import { CipoFormModule } from '@shared/modules/forms.module';
import { Observable, debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs';
import { CipoTextViewComponent } from '../cipo-text-view/cipo-text-view.component';
import { CipoAddressService } from './cipo-address.service';

@Component({
  selector: 'cipo-address',
  imports: [CipoFormModule, CipoTextViewComponent],
  templateUrl: './cipo-address.component.html',
  styles: [
    `
      // To be removed when AngularJS forms are no longer supported
      .mat-mdc-form-field.multiple-values.angularjs-support ::ng-deep .mdc-text-field--filled {
        .mat-mdc-form-field-infix {
          padding-top: 16px;
        }
        .mdc-floating-label.mdc-floating-label--float-above {
          transform: translateY(-124%) scale(0.75);
        }
        .mdc-floating-label:not(.mdc-floating-label--float-above) {
          transform: translateY(-40%);
        }
      }
    `,
  ],
})
export class CipoAddressComponent extends CipoControlBase<
  AddressModel | AddressModel[] | undefined,
  CipoAddressConfig
> {
  private dialog = inject(MatDialog);
  private azureMapService = inject(AzureMapService);
  addressService = inject(CipoAddressService);

  // To be removed when AngularJS forms are no longer supported
  angularJsSupport = input<boolean>(false);
  angularJsSupportCellField = input<boolean>(false);

  displayType = computed(() => this.fieldData()?.displayType);

  /** True when the field is configured as inline autocomplete (DisplayType 21) — no map dialog. */
  autocomplete = computed(() => this.displayType() === DisplayType.AddressAutocomplete);

  angularJsViewValue = computed(() => {
    const controlValue = this.control()?.value;
    if (typeof controlValue === 'string') {
      return controlValue;
    }
    return this.addresses()
      .map(addr => this.addressService.formatAddress(addr, this.displayType()))
      .join(', ');
  });

  addresses = computed(() =>
    Array.isArray(this.formValue())
      ? (this.formValue() as AddressModel[]).filter(addr => !!addr)
      : this.formValue()
        ? [this.formValue() as AddressModel]
        : [],
  );

  displaySingleValueControl = computed(() => {
    const control = new FormControl('');
    this.updateValidators(control);
    return control;
  });

  /**
   * Stable text input control for the autocomplete search field.
   * Not a computed signal — keeps a single instance so valueChanges subscription stays valid.
   */
  readonly autocompleteSearchControl = new FormControl<string>('');

  /** Address suggestions stream driven by the search control. */
  addressSuggestions$: Observable<AzureMapAddressSearchResult[]>;

  formValueChangeEffect = effect(() => {
    const multiple = this.fieldData()?.multiple ?? false;
    const value = this.addresses()[0];
    const formatted = this.addressService.formatAddress(value, this.displayType()) ?? '';
    this.displaySingleValueControl().patchValue(formatted);
    // In multiple mode the chip input stays empty — the chips themselves show the values
    if (!multiple) {
      this.autocompleteSearchControl.patchValue(formatted, { emitEvent: false });
    }
  });

  constructor() {
    super();

    // Keep touched state in sync for both display controls
    effect(() => {
      const displayControl = untracked(this.displaySingleValueControl);
      if (this.controlTouched()) {
        displayControl.markAsTouched();
        this.autocompleteSearchControl.markAsTouched();
      } else {
        displayControl.markAsUntouched();
        this.autocompleteSearchControl.markAsUntouched();
      }
    });

    // Wire the autocomplete suggestions stream to the stable search control
    this.addressSuggestions$ = this.autocompleteSearchControl.valueChanges.pipe(
      debounceTime(DEBOUNCE_TIMES.default),
      distinctUntilChanged(),
      filter(value => !!value && value.length > 0),
      switchMap(value => this.azureMapService.getAddressSuggestions(value)),
    );
  }

  /** Returns the display string for an autocomplete suggestion option. */
  getAddressText(option: AzureMapAddressSearchResult | string): string {
    if (typeof option === 'string') {
      return option;
    }
    return option?.address?.freeformAddress ?? '';
  }

  /** Called when the user picks a suggestion from the autocomplete panel. */
  onAutocompleteOptionSelected(event: MatAutocompleteSelectedEvent, inputElement?: HTMLInputElement) {
    const { address, position } = event.option.value as AzureMapAddressSearchResult;
    const addressModel = { ...getAddressModelFromAzureMapAddressAndPosition(address, position), locationName: '-' };
    const multiple = this.fieldData()?.multiple ?? false;
    this.control()?.markAsTouched();
    if (multiple) {
      const current = this.addresses().filter(a => !!a);
      this.control()?.patchValue([...current, addressModel]);
      // Clear selected option state and input text for the next search.
      event.option.deselect();
      setTimeout(() => {
        this.autocompleteSearchControl.setValue('', { emitEvent: false });
        if (inputElement) {
          inputElement.value = '';
        }
      });
    } else {
      this.control()?.patchValue(addressModel);
    }
  }

  /** Removes one address from the multiple-value list by index. */
  removeAutocompleteAddress(index: number) {
    const updated = this.addresses().filter((_, i) => i !== index);
    this.control()?.patchValue(updated.length ? updated : undefined);
    this.control()?.markAsTouched();
  }

  deleteAddress() {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        title: 'MODULE_DEF_ADDRESS.DELETE_TITLE',
        messages: ['MODULE_DEF_ADDRESS.DELETE_MESSAGE'],
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.control()?.setValue(undefined);
      }
    });
  }

  open(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    const readonlyMapMode = this.autocomplete();
    const addresses = this.addresses();
    if (readonlyMapMode && !addresses.length) {
      return;
    }

    const multiple = this.fieldData()?.multiple ?? false;

    // Mark as touched when opening the dialog
    this.control()?.markAsTouched();

    const config = readonlyMapMode ? ({ ...this.fieldData(), readonly: true } as CipoAddressConfig) : this.fieldData();

    this.addressService.openMap(config, addresses).subscribe(changes => {
      if (readonlyMapMode || !changes) {
        return;
      }

      if (multiple) {
        this.control()?.patchValue([...changes]);
      } else {
        this.control()?.patchValue((changes ?? [])[0]);
      }
    });
  }
}
